import { Task } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { ET_TIMEZONE, LOCATIONS_REQUIRING_ANESTHESIA_BOOKING, MilestoneCode } from './constants';
import {
  LabSummary,
  LabsReviewed,
  LookupSource,
  PreProcedureInputs,
  ProcedureAppointment,
  RxSummary,
  TaskRef,
  TasksByCode,
} from './types';

export type ClearanceLabel = 'Obtained' | 'Pending' | 'Request Sent' | 'Not Required';
export type AnesthesiaLabel = 'Booked' | 'Pending' | 'Not Required';

export const CLEARANCE_LABEL_TO_STATUS: Record<ClearanceLabel, Task['status']> = {
  Obtained: 'completed',
  Pending: 'requested',
  'Request Sent': 'in-progress',
  'Not Required': 'cancelled',
};
export const ANESTHESIA_LABEL_TO_STATUS: Record<AnesthesiaLabel, Task['status']> = {
  Booked: 'completed',
  Pending: 'requested',
  'Not Required': 'cancelled',
};

export function clearanceLabel(status: Task['status'] | undefined): ClearanceLabel | null {
  const entry = Object.entries(CLEARANCE_LABEL_TO_STATUS).find(([, s]) => s === status);
  return (entry?.[0] as ClearanceLabel | undefined) ?? null;
}

export function anesthesiaLabel(status: Task['status'] | undefined): AnesthesiaLabel | null {
  const entry = Object.entries(ANESTHESIA_LABEL_TO_STATUS).find(([, s]) => s === status);
  return (entry?.[0] as AnesthesiaLabel | undefined) ?? null;
}

/** Retool tri-state: no task → null, completed → true, any other task → false. */
export function checkboxValue(task: TaskRef | undefined): boolean | null {
  if (!task) {
    return null;
  }
  return task.status === 'completed';
}

const DONE_STATUSES: ReadonlySet<string | undefined> = new Set(['completed', 'cancelled']);

export function toEt(iso: string | null | undefined): dayjs.Dayjs | null {
  if (!iso) {
    return null;
  }
  const d = dayjs(iso);
  return d.isValid() ? d.tz(ET_TIMEZONE) : null;
}

interface BaseRow {
  /** `${appointmentId}|${patientId}` */
  id: string;
  appointmentId: string;
  patientId: string;
  patientName: string | null;
  patientDOB: string | null;
  procedureDate: string | null;
  providerName: string | null;
  locationName: string | null;
  typeCode: string;
  typeDisplay: string;
  navigatorName: string | null;
  tasks: TasksByCode;
  issues: string[];
}

export interface ClinicalRow extends BaseRow {
  rx: RxSummary | null;
  labs: LabSummary | null;
  labsReviewed: LabsReviewed | null;
  clearanceStatus: ClearanceLabel | null;
  clearanceNote: string | null;
  infoCollected: boolean | null;
  needsMonitoring: boolean | null;
}

export interface LogisticalRow extends BaseRow {
  /** YYYY-MM-DD in ET. */
  procedureDateEt: string | null;
  appointmentComment: string | null;
  providerId: string | null;
  locationId: string | null;
  milestones: Partial<Record<MilestoneCode, string>>;
  ppd1Warning: boolean;
  scaleShippedAt: string | null;
  scaleOrdered: boolean | null;
  facilityPaperwork: boolean | null;
  labsSent: boolean | null;
  anesthesia: { label: AnesthesiaLabel | null; required: boolean; task: TaskRef | undefined };
}

/** A reason is only raised from data we actually read; an unreadable source proves nothing is missing. */
function isKnown(inputs: PreProcedureInputs): (source: LookupSource) => boolean {
  const unavailable = new Set(inputs.unavailable);
  return (source) => !unavailable.has(source);
}

function sortedAppointments(inputs: PreProcedureInputs): ProcedureAppointment[] {
  return [...inputs.appointments].sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
}

function baseRow(inputs: PreProcedureInputs, a: ProcedureAppointment): Omit<BaseRow, 'issues'> {
  return {
    id: `${a.appointmentId}|${a.patientId}`,
    appointmentId: a.appointmentId,
    patientId: a.patientId,
    patientName: a.patientName,
    patientDOB: inputs.birthDates[a.patientId] ?? null,
    procedureDate: a.start,
    providerName: a.providerName,
    locationName: a.locationName,
    typeCode: a.typeCode,
    typeDisplay: a.typeDisplay,
    navigatorName: inputs.navigators[a.patientId] ?? null,
    tasks: inputs.tasks[a.patientId] ?? {},
  };
}

/** Retool `clinicalChecklist_medplum` + tableClinical "needs attention" rules. */
export function buildClinicalRows(inputs: PreProcedureInputs): ClinicalRow[] {
  return sortedAppointments(inputs).map((a) => {
    const base = baseRow(inputs, a);
    const rx = inputs.rx[a.patientId] ?? null;
    const labs = inputs.labs[a.patientId] ?? null;
    const labsReviewed = inputs.labsReviewed[a.patientId] ?? null;
    const clearance = base.tasks.clearance;
    const reviewed = !!labsReviewed?.date;
    const known = isKnown(inputs);
    const labsKnown = known('labs') && known('labsReviewed');
    const issues = [
      known('rx') && !rx?.count ? 'Rx orders missing' : null,
      labsKnown && !labs?.aggregatedStatus && !reviewed ? 'Lab orders missing' : null,
      labsKnown && labs?.aggregatedStatus !== 'not_required' && !reviewed ? 'Lab results not reviewed' : null,
      known('tasks') && !DONE_STATUSES.has(clearance?.status) ? 'Clearance pending' : null,
    ].filter((x): x is string => !!x);
    return {
      ...base,
      issues,
      rx,
      labs,
      labsReviewed,
      clearanceStatus: clearanceLabel(clearance?.status),
      clearanceNote: clearance?.note ?? null,
      infoCollected: checkboxValue(base.tasks.infoCollected),
      needsMonitoring: checkboxValue(base.tasks.needsMonitoring),
    };
  });
}

/** Retool `logisticalChecklist` + tableLogistical "needs attention" rules. */
export function buildLogisticalRows(inputs: PreProcedureInputs): LogisticalRow[] {
  return sortedAppointments(inputs).map((a) => {
    const base = baseRow(inputs, a);
    const milestones = inputs.milestones[a.patientId] ?? {};
    const scaleShippedAt = inputs.scales[a.patientId] ?? null;
    const required = !!a.locationName && LOCATIONS_REQUIRING_ANESTHESIA_BOOKING.has(a.locationName);
    const anesthesiaTask = base.tasks.anesthesiaBooking;
    const anesthesiaStatus = anesthesiaTask?.status ?? (required ? undefined : 'cancelled');
    const facility = base.tasks.facilityPaperwork;
    const procedureEt = toEt(a.start);
    const ppd1 = toEt(milestones['post-procedure-wellness-check']);
    const ppd1Warning = !!ppd1 && !!procedureEt && ppd1.startOf('day').diff(procedureEt.startOf('day'), 'day') !== 1;
    const known = isKnown(inputs);
    const issues = [
      known('scales') && !scaleShippedAt ? 'Scale not shipped' : null,
      known('milestones') && !milestones['initial-wellness-visit'] ? 'TWE visit missing' : null,
      known('milestones') && !milestones['group-visit-with-your-doctor'] ? 'MD group visit missing' : null,
      known('milestones') && !milestones['iv-therapy'] ? 'IV not scheduled' : null,
      known('milestones') && !milestones['post-procedure-wellness-check'] ? 'PPD1 not scheduled' : null,
      known('tasks') && !DONE_STATUSES.has(facility?.status) ? 'Facility not booked' : null,
      known('tasks') && !DONE_STATUSES.has(anesthesiaStatus) ? 'Anesthesia not booked' : null,
    ].filter((x): x is string => !!x);
    return {
      ...base,
      issues,
      procedureDateEt: procedureEt?.format('YYYY-MM-DD') ?? null,
      appointmentComment: a.comment,
      providerId: a.providerId,
      locationId: a.locationId,
      milestones,
      ppd1Warning,
      scaleShippedAt,
      scaleOrdered: checkboxValue(base.tasks.scaleOrdered),
      facilityPaperwork: checkboxValue(facility),
      labsSent: checkboxValue(base.tasks.labsSent),
      anesthesia: { label: anesthesiaLabel(anesthesiaStatus), required, task: anesthesiaTask },
    };
  });
}

export type ClinicalSegment = 'all' | 'labs-missing' | 'labs-pending' | 'labs-not-reviewed' | 'clearance-pending';

/** Retool segmentedControlClinical filter sets. */
export function filterClinical(rows: ClinicalRow[], segment: ClinicalSegment): ClinicalRow[] {
  switch (segment) {
    case 'labs-missing':
      return rows.filter((r) => !r.labs?.aggregatedStatus && !r.labsReviewed?.date);
    case 'labs-pending':
      return rows.filter(
        (r) =>
          !!r.labs?.aggregatedStatus &&
          r.labs.aggregatedStatus !== 'completed' &&
          r.labs.aggregatedStatus !== 'not_required'
      );
    case 'labs-not-reviewed':
      return rows.filter((r) => r.labs?.aggregatedStatus === 'completed' && !r.labsReviewed?.date);
    case 'clearance-pending':
      return rows.filter((r) => r.clearanceStatus !== 'Obtained' && r.clearanceStatus !== 'Not Required');
    default:
      return rows;
  }
}

export type LogisticalSegment = 'all' | 'missing-visits' | 'facility-anesthesia';

/** Retool segmentedControlLogistical filter sets (both OR-joined). */
export function filterLogistical(rows: LogisticalRow[], segment: LogisticalSegment): LogisticalRow[] {
  switch (segment) {
    case 'missing-visits':
      return rows.filter(
        (r) =>
          !r.milestones['initial-wellness-visit'] ||
          !r.milestones['group-visit-with-your-doctor'] ||
          !r.milestones['iv-therapy'] ||
          !r.milestones['post-procedure-wellness-check']
      );
    case 'facility-anesthesia':
      return rows.filter(
        (r) => !r.facilityPaperwork || r.anesthesia.label === null || r.anesthesia.label === 'Pending'
      );
    default:
      return rows;
  }
}
